import { Search, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { customerRepository } from '../../db/repositories';
import type { CustomerRecord } from '../../db/types';
import { maskMobile } from '../../domain/customers/customerUtils';

type CustomerValue = {
  id?: string;
  name: string;
  mobileNumber?: string;
  address?: string;
  notes?: string;
};
type Props = {
  initial?: CustomerValue;
  onSave: (customer: CustomerValue) => void;
  onClose: () => void;
};

export function CustomerDialog({ initial, onSave, onClose }: Props) {
  const [id, setId] = useState(initial?.id);
  const [name, setName] = useState(initial?.name ?? '');
  const [mobile, setMobile] = useState(initial?.mobileNumber ?? '');
  const [address, setAddress] = useState(initial?.address ?? '');
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [query, setQuery] = useState('');
  const [matches, setMatches] = useState<CustomerRecord[]>([]);

  useEffect(() => {
    if (query.trim()) void customerRepository.search(query).then(setMatches);
    else setMatches([]);
  }, [query]);
  const select = (customer: CustomerRecord) => {
    setId(customer.id);
    setName(customer.name);
    setMobile(customer.mobileNumber ?? '');
    setAddress(customer.address ?? '');
    setNotes(customer.notes ?? '');
    setQuery('');
    setMatches([]);
  };
  const save = () => {
    if (name.trim())
      onSave({
        id,
        name: name.trim(),
        mobileNumber: mobile.trim() || undefined,
        address: address.trim() || undefined,
        notes: notes.trim() || undefined,
      });
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-950/40 p-5">
      <div className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <div>
            <p className="text-[10px] font-extrabold uppercase tracking-widest text-[#00549C]">
              Customer
            </p>
            <h2 className="text-xl font-black text-[#003B70]">Lookup or add customer</h2>
          </div>
          <button
            onClick={onClose}
            className="grid h-11 w-11 place-items-center rounded-xl bg-slate-100"
            aria-label="Close"
          >
            <X />
          </button>
        </div>
        <div className="grid gap-4 p-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search existing customer by name, code, or mobile"
              className="h-13 w-full rounded-xl border border-slate-200 pl-10 pr-4 font-semibold outline-none focus:border-[#00549C]"
            />
            {matches.length > 0 && (
              <div className="absolute left-0 right-0 top-14 z-10 max-h-56 overflow-auto rounded-xl border border-slate-200 bg-white p-2 shadow-xl">
                {matches.map((customer) => (
                  <button
                    key={customer.id}
                    onClick={() => select(customer)}
                    className="w-full rounded-lg p-3 text-left hover:bg-[#DCEEFF]"
                  >
                    <p className="font-black text-slate-800">{customer.name}</p>
                    <p className="text-xs font-semibold text-slate-400">
                      {customer.customerCode ?? 'Legacy'}{' '}
                      {customer.mobileNumber ? `• ${maskMobile(customer.mobileNumber)}` : ''}
                    </p>
                  </button>
                ))}
              </div>
            )}
          </div>
          {id && (
            <p className="rounded-xl bg-[#DCEEFF] p-3 text-xs font-black text-[#00549C]">
              Existing customer selected. This order will use the saved customer record.
            </p>
          )}
          <label className="text-sm font-bold text-slate-700">
            Full name
            <input
              autoFocus
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-2 h-14 w-full rounded-xl border border-slate-200 px-4 text-lg font-semibold outline-none focus:border-[#00549C]"
            />
          </label>
          <label className="text-sm font-bold text-slate-700">
            Mobile number
            <input
              type="tel"
              inputMode="tel"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              className="mt-2 h-14 w-full rounded-xl border border-slate-200 px-4 text-lg font-semibold outline-none focus:border-[#00549C]"
            />
          </label>
          <label className="text-sm font-bold text-slate-700">
            Address
            <input
              type="text"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              className="mt-2 h-14 w-full rounded-xl border border-slate-200 px-4 font-semibold outline-none focus:border-[#00549C]"
            />
          </label>
          <label className="text-sm font-bold text-slate-700">
            Notes
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="mt-2 min-h-24 w-full rounded-xl border border-slate-200 p-4 font-medium outline-none focus:border-[#00549C]"
            />
          </label>
          <button
            type="button"
            onClick={save}
            disabled={!name.trim()}
            className="min-h-[64px] rounded-xl bg-[#00549C] text-lg font-black text-white disabled:opacity-40"
          >
            {id ? 'USE CUSTOMER' : 'ADD CUSTOMER'}
          </button>
        </div>
      </div>
    </div>
  );
}
